// Define the Role interface
import { User } from '@auth0/auth0-react';
import { getStoredAccessToken } from './auth0Utils';

export interface Role {
  is_system_role: boolean;
  role_type: string;
  id: string;
  name: string;
  description?: string;
}

/**
 * Fetches user roles from Auth0 Management API with caching
 * @param user The Auth0 user object
 * @returns Array of user roles
 */
// Define the Permission interface
export interface Permission {
  id: string;
  name: string;
  action: string;
  entity_type: string;
}

// Function to decode JWT token
// Define interfaces for decoded token
interface DecodedTokenRole {
  id: string;
  name: string;
  description?: string;
  is_system_role?: boolean;
  role_type?: string;
}

interface DecodedTokenPermission {
  id: string;
  name: string;
  action: string;
  entity_type: string;
}

interface DecodedToken {
  roles?: DecodedTokenRole[];
  permissions?: DecodedTokenPermission[];
  company?: {
    id: string;
    name: string;
  };
  [key: string]: unknown;
}

function decodeJwt(token: string): DecodedToken | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    const decoded = JSON.parse(jsonPayload);

    // Log the entire decoded token to see what properties it contains
    console.log('Decoded JWT token:', JSON.stringify(decoded, null, 2));

    return decoded;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Extracts roles and permissions from the JWT token
 * @param accessToken The JWT access token
 * @returns Object containing roles and permissions arrays
 *
 *
 */ // This function extracts roles and permissions from a JWT access token.

export function extractRolesAndPermissions(accessToken: string): {
  roles: Role[];
  permissions: Permission[];
  is_superuser?: boolean;
} {
  try {
    console.log('Decoding access token:', accessToken);
    const decodedToken = decodeJwt(accessToken);

    if (!decodedToken) {
      throw new Error('Failed to decode token');
    }

    // Extract roles from the token
    const roles = Array.isArray(decodedToken.roles)
      ? decodedToken.roles.map((role: DecodedTokenRole) => ({
          id: role.id,
          name: role.name,
          description: role.description,
        }))
      : [];

    // Extract permissions from the token
    const permissions = Array.isArray(decodedToken.permissions)
      ? decodedToken.permissions.map((permission: DecodedTokenPermission) => ({
          id: permission.id,
          name: permission.name,
          action: permission.action,
          entity_type: permission.entity_type,
        }))
      : [];

    return {
      roles,
      permissions,
      is_superuser: decodedToken.is_superuser,
    };
  } catch (error) {
    console.error('Error extracting roles and permissions from token:', error);
    return { roles: [], permissions: [] };
  }
}

export async function getUsersRoles(
  user: User | undefined,
  accessToken?: string
): Promise<Role[]> {
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Ensure we have a valid user ID
  const userId = user.sub;
  if (!userId) {
    throw new Error('User ID is missing');
  }

  try {
    console.log('Extracting roles from token for user:', userId);

    // Get access token - either from parameter or from utility function
    const token = accessToken || getStoredAccessToken();

    if (!token) {
      throw new Error('Access token not found');
    }

    // Extract roles from the token
    const { roles } = extractRolesAndPermissions(token);

    return roles;
  } catch (error) {
    console.error('Error getting roles:', error);
    return [];
  }
}

/**
 * Gets user roles and permissions from the access token
 * @param accessToken The JWT access token
 * @returns Object containing roles and permissions arrays
 */
export function getUsersRolesAndPermissions(accessToken: string): {
  roles: Role[];
  permissions: Permission[];
} {
  if (!accessToken) {
    return { roles: [], permissions: [] };
  }

  return extractRolesAndPermissions(accessToken);
}

/**
 * Checks if the user has a specific role
 * @param roles Array of user roles
 * @param roleName Role name to check for
 * @returns True if the user has the role, false otherwise
 */
export function hasRole(roles: Role[], roleName: string): boolean {
  return roles.some(
    (role) => role.name.toLowerCase() === roleName.toLowerCase()
  );
}

/**
 * Checks if the user has a specific permission
 * @param permissions Array of user permissions
 * @param permissionName Permission name to check for
 * @returns True if the user has the permission, false otherwise
 */
export function hasPermission(
  permissions: Permission[],
  permissionName: string
): boolean {
  return permissions.some(
    (permission) =>
      permission.name.toLowerCase() === permissionName.toLowerCase()
  );
}

/**
 * Checks if the user is a SuperAdmin
 * @param roles Array of user roles
 * @returns True if the user is a SuperAdmin, false otherwise
 */
export function isSuperAdmin(roles: Role[]): boolean {
  // Check for 'superadmin' role (case-insensitive)
  return roles.some((role) => role.name.toLowerCase() === 'superadmin');
}

/**
 * Checks if the user is an Admin
 * @param roles Array of user roles
 * @returns True if the user is an Admin, false otherwise
 */
export function isAdmin(roles: Role[]): boolean {
  // Check for 'admin' role (case-insensitive)
  return roles.some((role) => role.name.toLowerCase() === 'admin');
}

export function extractUserDetailsFromToken(accessToken: string): {
  roles: Role[];
  permissions: Permission[];
  company?: { id: string; name: string };
  is_superuser?: boolean;
} {
  try {
    const decodedToken = decodeJwt(accessToken);

    if (!decodedToken) {
      throw new Error('Failed to decode token');
    }

    const roles = Array.isArray(decodedToken.roles)
      ? decodedToken.roles.map((role) => ({
          id: role.id,
          name: role.name,
          description: role.description,
          is_system_role: role.is_system_role,
          role_type: role.role_type,
        }))
      : [];

    const permissions = Array.isArray(decodedToken.permissions)
      ? decodedToken.permissions.map((permission) => ({
          id: permission.id,
          name: permission.name,
          action: permission.action,
          entity_type: permission.entity_type,
        }))
      : [];

    const company = decodedToken.company
      ? {
          id: (decodedToken.company as any).id,
          name: (decodedToken.company as any).name,
        }
      : undefined;

    return {
      roles,
      permissions,
      company,
      is_superuser: decodedToken.is_superuser,
    };
  } catch (error) {
    console.error('Error extracting user info from token:', error);
    return { roles: [], permissions: [] };
  }
}
